import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { ThemeService } from '../../../../shared/services/utils/theme.service';
import { FluxoCaixaService } from '../../../../shared/services/apis/financas/fluxo-caixa.service';
import { FluxoCaixaTipoDetalheEnum } from '../../../../shared/enums/financas/fluxo-caixa-tipo-detalhe.enum';
import { FluxoCaixaDetalheDiarioDialogComponent, FluxoCaixaDetalheDiarioDialogData } from './components/fluxo-caixa-detalhe-diario-dialog/fluxo-caixa-detalhe-diario-dialog.component';
import { FluxoCaixaRequest } from '../../../../shared/services/requests/financas/fluxo-caixa.request';
import { SaveRelatorioFiltroFavoritoRequest } from '../../../../shared/services/requests/financas/save-relatorio-filtro-favorito.request';
import { RelatorioFiltroFavorito } from '../../../../shared/services/responses/financas/relatorio-filtro-favorito.response';
import {
    ContaFinanceiraFluxoItem,
    CentroCustoItem,
    FluxoCaixaDiarioItem,
    FluxoCaixaDiarioResponse,
    FluxoCaixaMensalMovimentacao,
    FluxoCaixaMensalCategoria,
    FluxoCaixaMensalResponse
} from '../../../../shared/services/responses/financas/fluxo-caixa.response';
import { NomeFavoritoModalComponent } from '../relatorio-entradas-saidas-cliente/components/nome-favorito-modal/nome-favorito-modal.component';

declare var google: any;

type Granularidade = 'diario' | 'mensal';

type FluxoCaixaChartColorMode = 'light' | 'dark';

/** Cores e estilos compartilhados entre os ComboCharts (diário e mensal). */
const FLUXO_CAIXA_CHART = {
    bar: {
        /** Mensal: Previsto = tom claro, Realizado = tom escuro */
        receitaPrevisto: '#81c784',   // verde claro (previsto)
        receitaRealizado: '#2e7d32',  // verde escuro (realizado)
        pagamentoPrevisto: '#f48fb1', // rosa claro (previsto)
        pagamentoRealizado: '#c62828',// vermelho escuro (realizado)
        /** Diário (legado): mantém compatibilidade */
        receita: '#4caf50',
        receitaClara: '#09630E',
        despesa: '#f44336',
        despesaClara: '#e57373'
    },
    saldo: {
        linhaDiario: { light: '#1e293b', dark: '#0E68DD' },
        /** Mensal: amarelo = previsto, azul = realizado */
        saldoPrevisto: { light: '#f59e0b', dark: '#fde047' },
        saldoRealizado: { light: '#2563eb', dark: '#60a5fa' }
    },
    ui: {
        light: {
            legend: '#374151',
            axis: '#4b5563',
            grid: '#e5e7eb',
            gridMinor: '#f3f4f6'
        },
        dark: {
            legend: '#e5e7eb',
            axis: '#cbd5e1',
            grid: 'rgba(255,255,255,0.18)',
            gridMinor: 'rgba(255,255,255,0.08)'
        }
    },
    /** Tooltip HTML do Google Charts: elemento solto inserido fora da árvore do componente — não herda
     *  estilo da página, precisa de cor explícita nos dois temas (senão fica ilegível no dark mode). */
    tooltip: {
        light: { background: '#ffffff', border: '#e5e7eb', label: '#6b7280', text: '#1f2937' },
        dark: { background: '#2a2a2a', border: '#404040', label: '#9ca3af', text: '#f4f4f4' }
    },
    line: {
        diario: {
            lineWidth: { light: 3, dark: 4 },
            pointSize: { light: 5, dark: 6 }
        },
        mensal: {
            lineWidth: { light: 3, dark: 4 },
            pointSize: { light: 4, dark: 5 }
        }
    },
    /** Google Charts não arredonda barras nativamente; `cornerRadiusPx` é aplicado no SVG no evento `ready`. */
    barPlot: {
        groupWidth: '82%',
        cornerRadiusPx: 4
    }
} as const;

@Component({
    selector: 'app-relatorio-fluxo-caixa',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatProgressSpinnerModule,
        MatSelectModule,
        MatTooltipModule,
        HeaderComponent,
        LoadingBoxComponent
    ],
    providers: [DatePipe],
    templateUrl: './relatorio-fluxo-caixa.component.html',
    styleUrl: './relatorio-fluxo-caixa.component.css'
})
export class RelatorioFluxoCaixaComponent implements OnInit, OnDestroy {
    public moduloEnum = ModuloEnum.Financas;
    public funcionalidadeEnum = FuncionalidadeEnum.Financas_Relatorios;

    public readonly granularidadeOpcoes = [
        { id: 'diario' as const, descricao: 'Diário' },
        { id: 'mensal' as const, descricao: 'Mensal' }
    ];

    public readonly nomesMeses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

    public form = this.fb.group({
        granularidade: ['diario' as Granularidade],
        mesAno: [this.getMesAnoAtual()],
        anoVisualizacao: [String(new Date().getFullYear())],
        idContaFinanceira: [null as number | null],
        idCentroCusto: [null as number | null],
        favoritoSelecionado: [null as string | null]
    });

    public carregandoLookups = true;
    public carregandoRelatorio = false;
    public readonly tipoDetalheEnum = FluxoCaixaTipoDetalheEnum;
    private filtroDiarioAtual: FluxoCaixaRequest | null = null;
    public filtrosVisiveis = true;
    public consultaGerada = false;
    public possuiResultados = false;
    public mensagemEmptyState = '';
    private googleChartsLoaded = false;

    public listaContasFinanceiras: ContaFinanceiraFluxoItem[] = [];
    public listaCentrosCusto: CentroCustoItem[] = [];
    public favoritos: RelatorioFiltroFavorito[] = [];

    public diarioResponse: FluxoCaixaDiarioResponse | null = null;

    /** Linhas da tabela diária: só dias com movimento realizado ou previsto. */
    // Dias sem movimento também aparecem (zerados) — o backend já traz todos os dias do mês
    // incondicionalmente; a coluna de Saldo precisa refletir o acumulado mesmo nos dias sem lançamento.
    public get diarioItensTabela(): FluxoCaixaDiarioItem[] {
        return this.diarioResponse?.itens ?? [];
    }

    public mensalResponse: FluxoCaixaMensalResponse | null = null;
    public categoriasReceitaColapsadas = new Set<number>();
    public categoriasDespesaColapsadas = new Set<number>();
    public categoriasReceitaDetalhesExpandidas = new Set<number>();
    public categoriasDespesaDetalhesExpandidas = new Set<number>();
    public matrizColapsadas = new Set<string>();
    public readonly mesesIndices = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    private readonly categoriaDrillPalette = [
        '#4f86c6', '#5ab47e', '#f5c842', '#9b6bb5', '#e8734a',
        '#3dc9b0', '#f0a05a', '#e85a75', '#5c7db3', '#a3c654'
    ];

    /** Matriz mensal sempre exibe jan…dez, conforme visão comparativa anual. */
    public get mensalMesesIndicesTabela(): number[] {
        return [...this.mesesIndices];
    }

    public periodoDescricaoGerado = '';
    private idTipoRelatorio: number | null = null;
    private readonly codigoRelatorio = 'FLUXO_CAIXA';
    private readonly favoritosStorageKey = `financas-${this.codigoRelatorio.toLowerCase()}-favoritos`;
    private readonly destroy$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private datePipe: DatePipe,
        private router: Router,
        private dialog: MatDialog,
        private alertService: AlertService,
        private fluxoCaixaService: FluxoCaixaService,
        private themeService: ThemeService
    ) { }

    public ngOnInit(): void {
        this.carregarLookups();
        this.loadGoogleCharts();

        // Google Charts desenha em canvas/SVG fora da árvore normal do Angular — não reage a CSS puro de
        // dark/light mode. Sem isto, o gráfico só pega as cores certas na próxima vez que "Gerar" for clicado.
        this.themeService.themeChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            if (this.possuiResultados) this.desenharGrafico();
        });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public voltar(): void {
        this.router.navigate(['/financas/relatorios']);
    }

    public toggleFiltros(): void {
        this.filtrosVisiveis = !this.filtrosVisiveis;
    }

    // Drill-down (US 139996): abre o detalhamento dos lançamentos que compõem um valor Realizado da
    // tabela "Movimentações Diárias".
    public abrirDetalheDiario(item: FluxoCaixaDiarioItem, tipo: FluxoCaixaTipoDetalheEnum, tituloTipo: string): void {
        if (!this.filtroDiarioAtual) return;
        // Mesmo tratamento de "data de calendário" usado em formatarData — descarta hora/fuso do ISO string.
        const dia = new Date(`${item.data.split('T')[0]}T00:00:00`).getDate();

        const data: FluxoCaixaDetalheDiarioDialogData = {
            filtros: {
                mes: this.filtroDiarioAtual.mes,
                ano: this.filtroDiarioAtual.ano,
                dia,
                tipo,
                idContaFinanceira: this.filtroDiarioAtual.idContaFinanceira,
                idCentroCusto: this.filtroDiarioAtual.idCentroCusto
            },
            tituloTipo,
            dataLabel: `${this.formatarData(item.data)} — ${item.diaSemana}`
        };

        this.dialog.open(FluxoCaixaDetalheDiarioDialogComponent, {
            width: '620px',
            maxWidth: '95vw',
            data
        });
    }

    // ─── Getters ─────────────────────────────────────────────────────────────────

    public get granularidadeAtual(): Granularidade {
        return (this.form.get('granularidade')?.value ?? 'diario') as Granularidade;
    }

    public get isDiario(): boolean {
        return this.granularidadeAtual === 'diario';
    }

    public get mesAnoControl(): FormControl<string> {
        return this.form.get('mesAno') as FormControl<string>;
    }

    public get anoVisualizacaoControl(): FormControl<string> {
        return this.form.get('anoVisualizacao') as FormControl<string>;
    }

    public get hasFavoritoSelecionado(): boolean {
        return !!this.favoritoSelecionadoAtual;
    }

    public get hasAlgumFiltroPreenchido(): boolean {
        return !!(this.form.get('idContaFinanceira')?.value || this.form.get('idCentroCusto')?.value);
    }

    public get favoritoSelecionadoAtual(): RelatorioFiltroFavorito | null {
        const id = this.form.get('favoritoSelecionado')?.value;
        return this.favoritos.find(f => String(f.idRelatorioFiltroFavorito) === id) || null;
    }

    // ─── Period ───────────────────────────────────────────────────────────────────

    public selecionarGranularidade(g: Granularidade): void {
        this.form.patchValue({ granularidade: g });
    }

    public navegarMes(passos: number): void {
        const ref = this.parseMesAno(this.mesAnoControl.value) ?? new Date();
        ref.setMonth(ref.getMonth() + passos);
        this.mesAnoControl.setValue(this.formatMesAno(ref));
    }

    public normalizarMesAno(): void {
        const norm = this.parseMesAno(this.mesAnoControl.value);
        this.mesAnoControl.setValue(norm ? this.formatMesAno(norm) : this.getMesAnoAtual());
    }

    public navegarAno(passos: number): void {
        const ano = Number(this.anoVisualizacaoControl.value) || new Date().getFullYear();
        this.anoVisualizacaoControl.setValue(String(ano + passos));
    }

    public normalizarAno(): void {
        const val = Number(this.anoVisualizacaoControl.value);
        if (!val || val < 2000 || val > 2100) {
            this.anoVisualizacaoControl.setValue(String(new Date().getFullYear()));
        }
    }

    // ─── Favorites ───────────────────────────────────────────────────────────────

    public favoritarFiltros(): void {
        if (this.hasFavoritoSelecionado) {
            this.removerFavoritoSelecionado();
            return;
        }

        const dialogRef = this.dialog.open(NomeFavoritoModalComponent, {
            width: '480px',
            maxWidth: '95vw',
            autoFocus: false,
            data: { nomeInicial: this.getProximoNomeFavorito() }
        });

        dialogRef.afterClosed().subscribe((nome: string | undefined) => {
            if (!nome?.trim()) return;
            this.salvarFavorito(nome.trim());
        });
    }

    public carregarFavoritoSelecionado(): void {
        const id = this.form.get('favoritoSelecionado')?.value;
        if (!id) { this.limparFiltrosFavorito(); return; }

        const favorito = this.favoritos.find(f => String(f.idRelatorioFiltroFavorito) === id);
        if (!favorito) { this.limparFiltrosFavorito(); return; }

        this.form.patchValue({
            mesAno: favorito.filtro.mesAno ?? this.getMesAnoAtual(),
            idContaFinanceira: favorito.filtro.idContaFinanceira ?? null
        });
    }

    public getFavoritoValue(favorito: RelatorioFiltroFavorito): string {
        return String(favorito.idRelatorioFiltroFavorito);
    }

    // ─── Lookup ───────────────────────────────────────────────────────────────────

    public carregarLookups(): void {
        this.carregandoLookups = true;
        this.fluxoCaixaService.getLookup().pipe(takeUntil(this.destroy$)).subscribe({
            next: (response) => {
                this.listaContasFinanceiras = response.listaContasFinanceiras || [];
                this.listaCentrosCusto = response.listaCentrosCusto || [];
                this.idTipoRelatorio = response.idTipoRelatorio ?? null;
                this.favoritos = response.listaFavoritos || [];
                if (!this.idTipoRelatorio) this.carregarFavoritosLocalStorage();
                this.carregandoLookups = false;
            },
            error: () => {
                this.carregandoLookups = false;
                this.alertService.show('Erro', 'Erro ao carregar os filtros do relatório.', AlertType.error);
            }
        });
    }

    // ─── Generate ────────────────────────────────────────────────────────────────

    public gerar(): void {
        if (!this.validarPeriodo()) return;

        this.periodoDescricaoGerado = this.getPeriodoDescricao();
        this.consultaGerada = true;
        this.carregandoRelatorio = true;
        this.mensagemEmptyState = '';
        this.diarioResponse = null;
        this.mensalResponse = null;

        const granularidade = this.granularidadeAtual;
        const request: FluxoCaixaRequest = {
            granularidade,
            mes: this.getMesSelecionado(),
            ano: this.getAnoSelecionado(),
            idContaFinanceira: this.form.get('idContaFinanceira')?.value ?? null,
            idCentroCusto: this.form.get('idCentroCusto')?.value ?? null
        };
        this.filtroDiarioAtual = request;

        if (granularidade === 'diario') {
            this.fluxoCaixaService.getDiario(request).pipe(takeUntil(this.destroy$)).subscribe({
                next: (response) => {
                    this.diarioResponse = response;
                    this.possuiResultados = (response.itens?.length ?? 0) > 0;
                    this.filtrosVisiveis = !this.possuiResultados;
                    this.mensagemEmptyState = this.possuiResultados ? '' : 'A consulta não retornou nenhum registro. Tente refazer a consulta com outros parâmetros.';
                    this.carregandoRelatorio = false;
                    if (this.possuiResultados) this.desenharGrafico();
                },
                error: () => {
                    this.possuiResultados = false;
                    this.filtrosVisiveis = true;
                    this.carregandoRelatorio = false;
                    this.alertService.show('Erro', 'Erro ao gerar o relatório.', AlertType.error);
                }
            });
        } else {
            this.fluxoCaixaService.getMensal(request).pipe(takeUntil(this.destroy$)).subscribe({
                next: (response) => {
                    this.mensalResponse = {
                        ...response,
                        categoriasReceita: (response.categoriasReceita || []).map(cat => ({
                            ...cat,
                            movimentacoes: (cat.movimentacoes || []).map(m => ({
                                ...m,
                                valor: Number(m.valor || 0)
                            }))
                        })),
                        categoriasDespesa: (response.categoriasDespesa || []).map(cat => ({
                            ...cat,
                            movimentacoes: (cat.movimentacoes || []).map(m => ({
                                ...m,
                                valor: Number(m.valor || 0)
                            }))
                        }))
                    };
                    this.possuiResultados = (response.resumoMensal?.length ?? 0) > 0;
                    this.filtrosVisiveis = !this.possuiResultados;
                    this.mensagemEmptyState = this.possuiResultados ? '' : 'A consulta não retornou nenhum registro. Tente refazer a consulta com outros parâmetros.';
                    this.categoriasReceitaColapsadas.clear();
                    this.categoriasDespesaColapsadas.clear();
                    this.categoriasReceitaDetalhesExpandidas.clear();
                    this.categoriasDespesaDetalhesExpandidas.clear();
                    this.matrizColapsadas.clear();
                    this.carregandoRelatorio = false;
                    if (this.possuiResultados) this.desenharGrafico();
                },
                error: () => {
                    this.possuiResultados = false;
                    this.filtrosVisiveis = true;
                    this.carregandoRelatorio = false;
                    this.alertService.show('Erro', 'Erro ao gerar o relatório.', AlertType.error);
                }
            });
        }
    }

    // ─── Drill-down: monthly row → daily view ─────────────────────────────────────

    public drillDownMes(mes: number): void {
        const anoStr = this.anoVisualizacaoControl.value;
        const ano = Number(anoStr) || new Date().getFullYear();
        const mesStr = String(mes).padStart(2, '0');
        this.form.patchValue({ granularidade: 'diario', mesAno: `${mesStr}/${ano}` });
        this.gerar();
    }

    // ─── Category tree ────────────────────────────────────────────────────────────

    public toggleCategoria(cat: FluxoCaixaMensalCategoria, tipo: 'receita' | 'despesa'): void {
        const set = tipo === 'receita' ? this.categoriasReceitaColapsadas : this.categoriasDespesaColapsadas;
        if (set.has(cat.idCategoriaFinanceira)) set.delete(cat.idCategoriaFinanceira);
        else set.add(cat.idCategoriaFinanceira);
    }

    public isCategoriaVisivel(cat: FluxoCaixaMensalCategoria, lista: FluxoCaixaMensalCategoria[], tipo: 'receita' | 'despesa'): boolean {
        const set = tipo === 'receita' ? this.categoriasReceitaColapsadas : this.categoriasDespesaColapsadas;
        let current: FluxoCaixaMensalCategoria = cat;
        while (current.idCategoriaFinanceiraPai) {
            if (set.has(current.idCategoriaFinanceiraPai)) return false;
            const parent = lista.find(c => c.idCategoriaFinanceira === current.idCategoriaFinanceiraPai);
            if (!parent) break;
            current = parent;
        }
        return true;
    }

    public temFilhos(cat: FluxoCaixaMensalCategoria, lista: FluxoCaixaMensalCategoria[]): boolean {
        return lista.some(c => c.idCategoriaFinanceiraPai === cat.idCategoriaFinanceira);
    }

    // Sobe a árvore até a raiz (nivel 0) — usado na exportação CSV pra separar "Categoria Pai" de
    // "Categoria Filha" sem depender de nivel === 1 (a árvore pode ter mais de 2 níveis).
    private getCategoriaRaizDescricao(cat: FluxoCaixaMensalCategoria, lista: FluxoCaixaMensalCategoria[]): string {
        let atual = cat;
        while (atual.idCategoriaFinanceiraPai) {
            const pai = lista.find(c => c.idCategoriaFinanceira === atual.idCategoriaFinanceiraPai);
            if (!pai) break;
            atual = pai;
        }
        return atual.descricao;
    }

    public isCategoriaColapsada(cat: FluxoCaixaMensalCategoria, tipo: 'receita' | 'despesa'): boolean {
        const set = tipo === 'receita' ? this.categoriasReceitaColapsadas : this.categoriasDespesaColapsadas;
        return set.has(cat.idCategoriaFinanceira);
    }

    public getCategoriaTotal(cat: FluxoCaixaMensalCategoria, campo: 'previstos' | 'realizados'): number {
        return (cat[campo] || []).reduce((s, v) => s + v, 0);
    }

    public getCatValor(cat: FluxoCaixaMensalCategoria, campo: 'previstos' | 'realizados', idx: number): number {
        return cat[campo]?.[idx] ?? 0;
    }

    public toggleDetalhesCategoria(cat: FluxoCaixaMensalCategoria, tipo: 'receita' | 'despesa'): void {
        const set = tipo === 'receita' ? this.categoriasReceitaDetalhesExpandidas : this.categoriasDespesaDetalhesExpandidas;
        if (set.has(cat.idCategoriaFinanceira)) set.delete(cat.idCategoriaFinanceira);
        else set.add(cat.idCategoriaFinanceira);
    }

    public isDetalhesCategoriaExpandido(cat: FluxoCaixaMensalCategoria, tipo: 'receita' | 'despesa'): boolean {
        const set = tipo === 'receita' ? this.categoriasReceitaDetalhesExpandidas : this.categoriasDespesaDetalhesExpandidas;
        return set.has(cat.idCategoriaFinanceira);
    }

    public getMovimentacoesCategoria(cat: FluxoCaixaMensalCategoria): FluxoCaixaMensalMovimentacao[] {
        if (!cat.movimentacoes?.length) return [];
        return [...cat.movimentacoes].sort(
            (a, b) => new Date(a.dataMovimentacao).getTime() - new Date(b.dataMovimentacao).getTime()
        );
    }

    public getCategoriaDrillColor(cat: FluxoCaixaMensalCategoria): string {
        const idx = Math.abs(Number(cat.idCategoriaFinanceira) || 0) % this.categoriaDrillPalette.length;
        return this.categoriaDrillPalette[idx];
    }

    // ─── Unified matrix (mensal) ──────────────────────────────────────────────────

    public toggleMatrizSecao(key: string): void {
        if (this.matrizColapsadas.has(key)) this.matrizColapsadas.delete(key);
        else this.matrizColapsadas.add(key);
    }

    public isSecaoColapsada(key: string): boolean {
        return this.matrizColapsadas.has(key);
    }

    /**
     * Retorna o resumo do mês pelo índice (0=jan), priorizando o campo `mes` da API.
     * Fallback por posição preserva compatibilidade com retornos legados.
     */
    private getResumoMensalByIndex(mesIndex: number) {
        if (!this.mensalResponse?.resumoMensal?.length) return null;
        const byMes = this.mensalResponse.resumoMensal.find(i => Number(i.mes) === mesIndex + 1);
        return byMes ?? this.mensalResponse.resumoMensal[mesIndex] ?? null;
    }

    public getSaldoMesAnteriorPrevisto(mesIndex: number): number {
        if (!this.mensalResponse) return 0;
        if (mesIndex === 0) return this.mensalResponse.saldoInicialAno;
        const prev = this.getResumoMensalByIndex(mesIndex - 1);
        return prev ? prev.saldoPrevisto : 0;
    }

    public getSaldoMesAnteriorRealizado(mesIndex: number): number {
        if (!this.mensalResponse) return 0;
        if (mesIndex === 0) return this.mensalResponse.saldoInicialAno;
        const prev = this.getResumoMensalByIndex(mesIndex - 1);
        return prev ? prev.saldoRealizado : 0;
    }

    public getMensalValor(campo: 'recebimentosPrevisto' | 'recebimentosRealizado' | 'pagamentosPrevisto' | 'pagamentosRealizado' | 'transferenciasEntrada' | 'transferenciasSaida' | 'saldoPrevisto' | 'saldoRealizado', mesIndex: number): number {
        if (!this.mensalResponse) return 0;
        const item = this.getResumoMensalByIndex(mesIndex);
        return item ? (item[campo] as number) : 0;
    }

    public scrollMatriz(ref: HTMLElement, dir: number): void {
        ref.scrollBy({ left: dir * 300, behavior: 'smooth' });
    }

    public getTotalMensal(campo: 'recebimentosPrevisto' | 'recebimentosRealizado' | 'pagamentosPrevisto' | 'pagamentosRealizado' | 'transferenciasEntrada' | 'transferenciasSaida'): number {
        if (!this.mensalResponse) return 0;
        return this.mensalResponse.resumoMensal.reduce((s, i) => s + (i[campo] as number), 0);
    }

    public getSaldoFinalAno(): number {
        if (!this.mensalResponse) return 0;
        const dezembro = this.getResumoMensalByIndex(11);
        if (dezembro) return dezembro.saldoRealizado;
        const last = this.mensalResponse.resumoMensal[this.mensalResponse.resumoMensal.length - 1];
        return last?.saldoRealizado ?? 0;
    }

    // ─── Google Charts ────────────────────────────────────────────────────────────

    private loadGoogleCharts(): void {
        const load = () => {
            google.charts.load('current', { packages: ['corechart'] });
            google.charts.setOnLoadCallback(() => { this.googleChartsLoaded = true; });
        };
        if (typeof google !== 'undefined' && google.charts) { load(); return; }
        const script = document.createElement('script');
        script.src = 'https://www.gstatic.com/charts/loader.js';
        script.onload = load;
        document.head.appendChild(script);
    }

    private getFluxoCaixaChartMode(): FluxoCaixaChartColorMode {
        return document.body.classList.contains('dark-mode') ? 'dark' : 'light';
    }

    private parseSvgFillToRgb(fill: string | null): [number, number, number] | null {
        if (!fill) return null;
        const f = fill.trim();
        const hex6 = /^#?([0-9a-f]{6})$/i.exec(f);
        if (hex6) {
            const n = parseInt(hex6[1], 16);
            return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
        }
        const rgb = f.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
        if (rgb) return [Math.round(Number(rgb[1])), Math.round(Number(rgb[2])), Math.round(Number(rgb[3]))];
        return null;
    }

    /** Cor efetiva do retângulo (atributo, style ou getComputedStyle) — o Charts às vezes só preenche via CSS. */
    private getSvgRectEffectiveFill(rect: SVGRectElement): string | null {
        const attr = rect.getAttribute('fill');
        if (attr && attr !== 'none' && !attr.toLowerCase().startsWith('url(')) {
            return attr.trim();
        }
        const styleAttr = rect.getAttribute('style');
        if (styleAttr) {
            const m = styleAttr.match(/(?:^|;)\s*fill:\s*([^;]+)/i);
            if (m) {
                const v = m[1].trim();
                if (v && v !== 'none' && !v.toLowerCase().startsWith('url(')) return v;
            }
        }
        try {
            const cs = window.getComputedStyle(rect).fill;
            if (cs && cs !== 'none' && !/^rgba\(\s*0\s*,\s*0\s*,\s*0\s*,\s*0\s*\)$/i.test(cs)) {
                return cs.trim();
            }
        } catch {
            /* ignore */
        }
        return null;
    }

    private isFluxoCaixaBarFill(fill: string | null): boolean {
        const a = this.parseSvgFillToRgb(fill);
        if (!a) return false;
        const tol = 8;
        for (const hex of Object.values(FLUXO_CAIXA_CHART.bar)) {
            const b = this.parseSvgFillToRgb(hex);
            if (!b) continue;
            if (
                Math.abs(a[0] - b[0]) <= tol &&
                Math.abs(a[1] - b[1]) <= tol &&
                Math.abs(a[2] - b[2]) <= tol
            ) {
                return true;
            }
        }
        return false;
    }

    private isFluxoCaixaBarRect(rect: SVGRectElement): boolean {
        return this.isFluxoCaixaBarFill(this.getSvgRectEffectiveFill(rect));
    }

    /** Filetes fantasma (série de saída com altura ~0) — cobre paleta diária e mensal. */
    private ocultarFiletesBarrasNegativas(container: HTMLElement): void {
        const bar = FLUXO_CAIXA_CHART.bar;
        // Cores de pagamento/despesa de ambas as paletas (diário + mensal)
        const coresDespesa = [bar.despesa, bar.despesaClara, bar.pagamentoPrevisto, bar.pagamentoRealizado];
        const limiarAltura = 2.8;
        container.querySelectorAll<SVGRectElement>('svg rect').forEach((rect) => {
            const fill = this.getSvgRectEffectiveFill(rect);
            if (!fill) return;
            const isDespesa = coresDespesa.some(hex => this.isBarColor(fill, hex, 12));
            if (!isDespesa) return;
            const h = rect.height?.baseVal?.value ?? 0;
            if (h > 0 && h <= limiarAltura) {
                rect.setAttribute('opacity', '0');
                rect.setAttribute('pointer-events', 'none');
            }
        });
    }

    private isBarColor(fill: string | null, hex: string, tol = 8): boolean {
        const a = this.parseSvgFillToRgb(fill);
        const b = this.parseSvgFillToRgb(hex);
        if (!a || !b) return false;
        return (
            Math.abs(a[0] - b[0]) <= tol &&
            Math.abs(a[1] - b[1]) <= tol &&
            Math.abs(a[2] - b[2]) <= tol
        );
    }

    /** No mensal: barras "realizado" ficam sobrepostas e mais estreitas dentro do "previsto". */
    private sobreporBarrasMensaisPrevistoRealizado(container: HTMLElement): void {
        const rects = Array.from(container.querySelectorAll<SVGRectElement>('svg rect'))
            .filter(r => this.isFluxoCaixaBarRect(r))
            .filter(r => (r.width?.baseVal?.value ?? 0) > 2 && (r.height?.baseVal?.value ?? 0) > 2);

        const byColor = (hex: string) => rects.filter(r => this.isBarColor(this.getSvgRectEffectiveFill(r), hex));
        const recPrev = byColor(FLUXO_CAIXA_CHART.bar.receitaClara);
        const recReal = byColor(FLUXO_CAIXA_CHART.bar.receita);
        const pagPrev = byColor(FLUXO_CAIXA_CHART.bar.despesaClara);
        const pagReal = byColor(FLUXO_CAIXA_CHART.bar.despesa);

        const sobrepor = (realizados: SVGRectElement[], previstos: SVGRectElement[]) => {
            for (const real of realizados) {
                const rx = real.x?.baseVal?.value ?? 0;
                const ry = real.y?.baseVal?.value ?? 0;
                const rw = real.width?.baseVal?.value ?? 0;
                const rh = real.height?.baseVal?.value ?? 0;
                const rCx = rx + rw / 2;

                const alvo = previstos
                    .filter(prev => {
                        const py = prev.y?.baseVal?.value ?? 0;
                        const ph = prev.height?.baseVal?.value ?? 0;
                        return !(ry + rh < py || py + ph < ry);
                    })
                    .sort((a, b) => {
                        const ax = (a.x?.baseVal?.value ?? 0) + (a.width?.baseVal?.value ?? 0) / 2;
                        const bx = (b.x?.baseVal?.value ?? 0) + (b.width?.baseVal?.value ?? 0) / 2;
                        return Math.abs(ax - rCx) - Math.abs(bx - rCx);
                    })[0];

                if (!alvo) continue;
                const px = alvo.x?.baseVal?.value ?? 0;
                const pw = alvo.width?.baseVal?.value ?? 0;
                const novoW = Math.max(3, pw * 0.62);
                const novoX = px + (pw - novoW) / 2;

                real.setAttribute('width', String(novoW));
                real.setAttribute('x', String(novoX));
            }
        };

        sobrepor(recReal, recPrev);
        sobrepor(pagReal, pagPrev);
    }

    private posProcessarBarrasComboChart(container: HTMLElement, overlayMensal = false): void {
        if (overlayMensal) this.sobreporBarrasMensaisPrevistoRealizado(container);
        this.ocultarFiletesBarrasNegativas(container);
        this.suavizarBordasBarrasComboChart(container);
        this.aplicarCantosBarrasComboChart(container, FLUXO_CAIXA_CHART.barPlot.cornerRadiusPx);
    }

    /** Cantos levemente arredondados nas barras (API clássica não expõe isso em opções). */
    private aplicarCantosBarrasComboChart(container: HTMLElement, radiusPx: number): void {
        container.querySelectorAll<SVGRectElement>('svg rect').forEach((rect) => {
            if (!this.isFluxoCaixaBarRect(rect)) return;
            const w = rect.width?.baseVal?.value ?? 0;
            const h = rect.height?.baseVal?.value ?? 0;
            if (w < 2 || h < 2 || w > 240) return;
            const r = Math.min(radiusPx, w / 2, h / 2);
            rect.setAttribute('rx', String(r));
            rect.setAttribute('ry', String(r));
        });
    }

    /**
     * Evita filete da cor da série seguinte (ex.: vermelho) na junção com o eixo ou entre pilhas:
     * traço na mesma cor do preenchimento, leve, com pintura por baixo do fill.
     */
    private suavizarBordasBarrasComboChart(container: HTMLElement): void {
        container.querySelectorAll<SVGRectElement>('svg rect').forEach((rect) => {
            if (!this.isFluxoCaixaBarRect(rect)) return;
            const w = rect.width?.baseVal?.value ?? 0;
            const h = rect.height?.baseVal?.value ?? 0;
            if (w < 2 || h < 2 || w > 240) return;
            const fill = this.getSvgRectEffectiveFill(rect) || '';
            rect.setAttribute('fill', fill);
            rect.setAttribute('stroke', fill);
            rect.setAttribute('stroke-width', '1.25');
            rect.setAttribute('stroke-opacity', '1');
            rect.setAttribute('paint-order', 'stroke fill');
        });
    }

    private desenharGrafico(): void {
        if (!this.googleChartsLoaded) { setTimeout(() => this.desenharGrafico(), 300); return; }
        setTimeout(() => {
            if (this.isDiario && this.diarioResponse) this.desenharGraficoDiario();
            else if (!this.isDiario && this.mensalResponse) this.desenharGraficoMensal();
        }, 100);
    }

    private desenharGraficoDiario(): void {
        const container = document.getElementById('chart-fluxo');
        if (!container || !this.diarioResponse) return;

        const mode   = this.getFluxoCaixaChartMode();
        const ui     = FLUXO_CAIXA_CHART.ui[mode];
        const bar    = FLUXO_CAIXA_CHART.bar;
        const line   = FLUXO_CAIXA_CHART.line.diario;
        const fmtBRL = (v: number) =>
            new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Math.abs(v));

        // Colunas empilhadas (soma do dia), com cores distinguindo realizado × previsto:
        // acima do eixo: Transf. Entrada (base) + Rec. Realizado + Rec. Previsto (topo);
        // abaixo: Transf. Saída (base) + Pag. Realizado + Pag. Previsto.
        const data = new google.visualization.DataTable();
        data.addColumn('string', 'Dia');
        data.addColumn('number', 'Transf. entrada');
        data.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });
        data.addColumn('number', 'Recebimentos realizados');
        data.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });
        data.addColumn('number', 'Recebimentos previstos');
        data.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });
        data.addColumn('number', 'Transf. saída');
        data.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });
        data.addColumn('number', 'Pagamentos realizados');
        data.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });
        data.addColumn('number', 'Pagamentos previstos');
        data.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });
        data.addColumn('number', 'Saldo previsto');
        data.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });
        data.addColumn('number', 'Saldo realizado');
        data.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });

        const r = this.diarioResponse;
        const ano = r.ano;
        const mes = r.mes;
        const ultimoDia = new Date(ano, mes, 0).getDate();

        const porDia = new Map<string, FluxoCaixaDiarioItem>();
        for (const item of r.itens) {
            const d = this.parseChartDate(item.data);
            if (!d) continue;
            const chave = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            porDia.set(chave, item);
        }

        let saldoReal = r.saldoInicial;
        let saldoPrev = r.saldoInicial;

        for (let dia = 1; dia <= ultimoDia; dia++) {
            const dt = new Date(ano, mes - 1, dia);
            const chave = `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
            const item = porDia.get(chave);
            const label = this.getDiarioChartLabel(dt, dt);

            const recPrev = item?.recebimentosPrevisto ?? 0;
            const recReal = item?.recebimentos ?? 0;
            const pagPrev = item?.pagamentosPrevisto ?? 0;
            const pagReal = item?.pagamentos ?? 0;
            const transfEnt = item?.transferenciasEntrada ?? 0;
            const transfSai = item?.transferenciasSaida ?? 0;
            saldoReal = item ? item.saldoFinal : saldoReal;
            saldoPrev = item ? item.saldoPrevisto : saldoPrev;

            const tooltipCores = FLUXO_CAIXA_CHART.tooltip[mode];
            const tip = (labelTip: string, val: number) =>
                `<div style="padding:8px 14px;font-family:Inter,Roboto,sans-serif;font-size:13px;min-width:210px;` +
                `background:${tooltipCores.background};color:${tooltipCores.text};border:1px solid ${tooltipCores.border}">` +
                `<strong style="display:block;margin-bottom:4px;font-size:11px;color:${tooltipCores.label}">${label}</strong>` +
                `${labelTip}: <strong>${fmtBRL(val)}</strong></div>`;

            // `null` omite o segmento no empilhamento — evita retângulos com altura 0 que geram filete
            const seg = (v: number): number | null => (v > 0 ? v : null);
            const segNeg = (v: number): number | null => (v > 0 ? -v : null);

            data.addRow([
                label,
                seg(transfEnt),    tip('Transf. entrada',          transfEnt),
                seg(recReal),      tip('Recebimentos realizados',  recReal),
                seg(recPrev),      tip('Recebimentos previstos',   recPrev),
                segNeg(transfSai), tip('Transf. saída',            transfSai),
                segNeg(pagReal),   tip('Pagamentos realizados',    pagReal),
                segNeg(pagPrev),   tip('Pagamentos previstos',     pagPrev),
                saldoPrev,         tip('Saldo previsto',           saldoPrev),
                saldoReal,         tip('Saldo realizado',          saldoReal),
            ]);
        }

        const chart = new google.visualization.ComboChart(container);
        google.visualization.events.addListener(chart, 'ready', () => {
            const run = () => this.posProcessarBarrasComboChart(container);
            run();
            requestAnimationFrame(run);
            setTimeout(run, 0);
            setTimeout(run, 120);
        });

        chart.draw(data, {
            height: 460,
            isStacked: true,
            legend: {
                position: 'top',
                alignment: 'start',
                maxLines: 3,
                textStyle: { color: ui.legend, fontSize: 12 }
            },
            seriesType: 'bars',
            series: {
                // series index = coluna numérica (colunas tooltip não contam)
                // Coluna do dia = soma; cores diferenciam realizado × previsto × transferência
                0: { color: bar.receita },            // verde médio  — Transf. Entrada (base)
                1: { color: bar.receitaRealizado },   // verde escuro — Rec. Realizado
                2: { color: bar.receitaPrevisto  },   // verde claro  — Rec. Previsto (topo)
                3: { color: bar.despesa },            // vermelho médio — Transf. Saída (base)
                4: { color: bar.pagamentoRealizado }, // vermelho escuro — Pag. Realizado
                5: { color: bar.pagamentoPrevisto },  // rosa         — Pag. Previsto
                6: {
                    type: 'line',
                    lineWidth: line.lineWidth[mode],
                    pointSize: line.pointSize[mode],
                    lineDashStyle: [6, 3],
                    color: FLUXO_CAIXA_CHART.saldo.saldoPrevisto[mode]
                },
                7: {
                    type: 'line',
                    lineWidth: line.lineWidth[mode] + 1,
                    pointSize: line.pointSize[mode],
                    color: FLUXO_CAIXA_CHART.saldo.saldoRealizado[mode]
                }
            },
            chartArea: { left: 72, right: 12, top: 72, bottom: 92 },
            vAxis: {
                format: '#,##0',
                textStyle: { color: ui.axis, fontSize: 11 },
                gridlines: { color: ui.grid },
                minorGridlines: { color: ui.gridMinor }
            },
            hAxis: {
                showTextEvery: 1,
                slantedText: true,
                slantedTextAngle: 45,
                textStyle: { color: ui.axis, fontSize: 11 }
            },
            bar: { groupWidth: FLUXO_CAIXA_CHART.barPlot.groupWidth },
            backgroundColor: 'transparent',
            tooltip: { isHtml: true },
            fontName: 'Inter, Roboto, Arial, sans-serif'
        });
    }

    private desenharGraficoMensal(): void {
        const container = document.getElementById('chart-fluxo');
        if (!container || !this.mensalResponse) return;

        const mode   = this.getFluxoCaixaChartMode();
        const ui     = FLUXO_CAIXA_CHART.ui[mode];
        const bar    = FLUXO_CAIXA_CHART.bar;
        const line   = FLUXO_CAIXA_CHART.line.mensal;
        const fmtBRL = (v: number) =>
            new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Math.abs(v));

        // Mês | RecPrev | tip | RecReal | tip | PagPrev | tip | PagReal | tip | SaldoPrev | tip | SaldoReal | tip
        const data = new google.visualization.DataTable();
        data.addColumn('string', 'Mês');
        data.addColumn('number', 'Recebimentos previstos');
        data.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });
        data.addColumn('number', 'Recebimentos realizados');
        data.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });
        data.addColumn('number', 'Pagamentos previstos');
        data.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });
        data.addColumn('number', 'Pagamentos realizados');
        data.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });
        data.addColumn('number', 'Saldo previsto');
        data.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });
        data.addColumn('number', 'Saldo realizado');
        data.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });

        const ano = this.mensalResponse.ano;
        const mesesAbrev = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

        for (const idx of this.mensalMesesIndicesTabela) {
            const item      = this.getResumoMensalByIndex(idx);
            const mesLabel  = `${mesesAbrev[idx]} '${String(ano).slice(-2)}`;
            const descricao = item?.descricaoMes ?? mesLabel;
            const recPrev   = item?.recebimentosPrevisto  ?? 0;
            const recReal   = item?.recebimentosRealizado ?? 0;
            const pagPrev   = item?.pagamentosPrevisto    ?? 0;
            const pagReal   = item?.pagamentosRealizado   ?? 0;
            const saldoPrev = item?.saldoPrevisto   ?? this.getSaldoMesAnteriorPrevisto(idx);
            const saldoReal = item?.saldoRealizado  ?? this.getSaldoMesAnteriorRealizado(idx);

            const tooltipCores = FLUXO_CAIXA_CHART.tooltip[mode];
            const tip = (label: string, val: number) =>
                `<div style="padding:8px 14px;font-family:Inter,Roboto,sans-serif;font-size:13px;min-width:210px;` +
                `background:${tooltipCores.background};color:${tooltipCores.text};border:1px solid ${tooltipCores.border}">` +
                `<strong style="display:block;margin-bottom:4px;font-size:11px;color:${tooltipCores.label}">${descricao}</strong>` +
                `${label}: <strong>${fmtBRL(val)}</strong></div>`;

            data.addRow([
                mesLabel,
                recPrev,            tip('Recebimentos previstos',  recPrev),
                recReal,            tip('Recebimentos realizados', recReal),
                -Math.abs(pagPrev), tip('Pagamentos previstos',    pagPrev),
                -Math.abs(pagReal), tip('Pagamentos realizados',   pagReal),
                saldoPrev,          tip('Saldo previsto',          saldoPrev),
                saldoReal,          tip('Saldo realizado',         saldoReal),
            ]);
        }

        const chart = new google.visualization.ComboChart(container);

        // Somente arredonda cantos e esconde filetes — SEM sobreposicao de barras
        google.visualization.events.addListener(chart, 'ready', () => {
            const run = () => {
                this.ocultarFiletesBarrasNegativas(container);
                this.aplicarCantosBarrasComboChart(container, 3);
            };
            run();
            requestAnimationFrame(run);
            setTimeout(run, 80);
        });

        chart.draw(data, {
            height: 440,
            isStacked: false,
            legend: {
                position: 'top',
                alignment: 'start',
                maxLines: 3,
                textStyle: { color: ui.legend, fontSize: 12 }
            },
            seriesType: 'bars',
            series: {
                // series index = coluna numérica (colunas tooltip não contam)
                0: { color: bar.receitaPrevisto  },   // verde claro  — Rec. Previsto
                1: { color: bar.receitaRealizado },   // verde escuro — Rec. Realizado
                2: { color: bar.pagamentoPrevisto },  // rosa         — Pag. Previsto
                3: { color: bar.pagamentoRealizado }, // vermelho     — Pag. Realizado
                4: {
                    type: 'line',
                    lineWidth: line.lineWidth[mode],
                    pointSize: line.pointSize[mode],
                    lineDashStyle: [6, 3],
                    color: FLUXO_CAIXA_CHART.saldo.saldoPrevisto[mode]   // cinza claro
                },
                5: {
                    type: 'line',
                    lineWidth: line.lineWidth[mode] + 1,
                    pointSize: line.pointSize[mode],
                    color: FLUXO_CAIXA_CHART.saldo.saldoRealizado[mode]  // cinza escuro/preto
                }
            },
            chartArea: { left: 72, right: 16, top: 80, bottom: 56 },
            vAxis: {
                format: '#,##0',
                textStyle: { color: ui.axis, fontSize: 11 },
                gridlines: { color: ui.grid },
                minorGridlines: { color: ui.gridMinor }
            },
            hAxis: { textStyle: { color: ui.axis, fontSize: 11 } },
            bar: { groupWidth: '80%' },
            backgroundColor: 'transparent',
            tooltip: { isHtml: true },
            fontName: 'Inter, Roboto, Arial, sans-serif'
        });
    }


    // ─── Export ───────────────────────────────────────────────────────────────────

    public imprimir(): void {
        if (!this.possuiResultados) return;
        const geradoEm = this.datePipe.transform(new Date(), 'dd/MM/yyyy HH:mm') || '';
        let conteudo = '';
        if (this.isDiario && this.diarioResponse) {
            conteudo = this.buildPrintDiario(geradoEm);
        } else if (!this.isDiario && this.mensalResponse) {
            conteudo = this.buildPrintMensal(geradoEm);
        }
        if (!conteudo) return;
        const popup = window.open('', '_blank', 'width=1200,height=900');
        if (!popup) return;
        popup.document.write(conteudo);
        popup.document.close();
        popup.focus();
        popup.print();
    }

    private buildPrintDiario(geradoEm: string): string {
        const r = this.diarioResponse!;
        const rows = this.diarioItensTabela.map(i => `<tr>
            <td>${this.formatarData(i.data)}</td><td>${i.diaSemana}</td>
            <td class="valor">${this.formatarMoeda(i.recebimentosPrevisto)}</td>
            <td class="valor">${this.formatarMoeda(i.recebimentos)}</td>
            <td class="valor">${this.formatarMoeda(i.pagamentosPrevisto)}</td>
            <td class="valor">${this.formatarMoeda(i.pagamentos)}</td>
            <td class="valor">${this.formatarMoeda(i.transferenciasEntrada)}</td>
            <td class="valor">${this.formatarMoeda(i.transferenciasSaida)}</td>
            <td class="valor">${this.formatarMoeda(i.saldoPrevisto)}</td>
            <td class="valor saldo ${i.saldoFinal < 0 ? 'negativo' : ''}">${this.formatarMoeda(i.saldoFinal)}</td>
        </tr>`).join('');
        return `<html><head><title>Fluxo de Caixa Diário — ${this.periodoDescricaoGerado}</title>
            <style>body{font-family:Arial,sans-serif;font-size:12px;padding:24px;color:#1f2937}h1{margin:0 0 6px;font-size:18px}.meta{color:#475569;font-size:11px;margin-bottom:16px}table{width:100%;border-collapse:collapse}th,td{border-bottom:1px solid #e5e7eb;padding:6px 8px;text-align:left}.valor{text-align:right}.negativo{color:#b73232}tfoot td{font-weight:700;border-top:2px solid #b73232}@media print{@page{size:landscape;margin:1cm}}</style>
            </head><body>
            <h1>Fluxo de Caixa Diário</h1>
            <div class="meta"><div>Período: ${this.periodoDescricaoGerado}</div><div>Saldo Inicial: ${this.formatarMoeda(r.saldoInicial)}</div><div>Gerado em: ${geradoEm}</div></div>
            <table><thead><tr><th>Data</th><th>Dia</th><th class="valor">Rec. Prev.</th><th class="valor">Rec. Real.</th><th class="valor">Pag. Prev.</th><th class="valor">Pag. Real.</th><th class="valor">Transf. Ent.</th><th class="valor">Transf. Saída</th><th class="valor">Saldo Prev.</th><th class="valor">Saldo Real.</th></tr></thead>
            <tbody>${rows}</tbody>
            <tfoot><tr><td colspan="2">TOTAL</td><td class="valor">${this.formatarMoeda(r.totalRecebimentosPrevisto)}</td><td class="valor">${this.formatarMoeda(r.totalRecebimentos)}</td><td class="valor">${this.formatarMoeda(r.totalPagamentosPrevisto)}</td><td class="valor">${this.formatarMoeda(r.totalPagamentos)}</td><td class="valor">${this.formatarMoeda(r.totalTransferenciasEntrada)}</td><td class="valor">${this.formatarMoeda(r.totalTransferenciasSaida)}</td><td class="valor">${this.formatarMoeda(r.saldoFinalPrevisto)}</td><td class="valor">${this.formatarMoeda(r.saldoFinal)}</td></tr></tfoot>
            </table></body></html>`;
    }

    private buildPrintMensal(geradoEm: string): string {
        const r = this.mensalResponse!;
        const rows = this.mensalMesesIndicesTabela
            .map(idx => this.getResumoMensalByIndex(idx))
            .filter((i): i is NonNullable<typeof i> => i !== null)
            .map(i => `<tr>
            <td>${i.descricaoMes}</td>
            <td class="valor">${this.formatarMoeda(i.recebimentosPrevisto)}</td>
            <td class="valor">${this.formatarMoeda(i.recebimentosRealizado)}</td>
            <td class="valor">${this.formatarMoeda(i.pagamentosPrevisto)}</td>
            <td class="valor">${this.formatarMoeda(i.pagamentosRealizado)}</td>
            <td class="valor">${this.formatarMoeda(i.transferenciasEntrada)}</td>
            <td class="valor">${this.formatarMoeda(i.transferenciasSaida)}</td>
            <td class="valor">${this.formatarMoeda(i.saldoPrevisto)}</td>
            <td class="valor ${i.saldoRealizado < 0 ? 'negativo' : ''}">${this.formatarMoeda(i.saldoRealizado)}</td>
        </tr>`).join('');
        return `<html><head><title>Fluxo de Caixa Mensal — ${this.periodoDescricaoGerado}</title>
            <style>body{font-family:Arial,sans-serif;font-size:11px;padding:24px;color:#1f2937}h1{font-size:18px;margin:0 0 6px}.meta{color:#475569;font-size:10px;margin-bottom:16px}table{width:100%;border-collapse:collapse}th,td{border-bottom:1px solid #e5e7eb;padding:5px 6px;text-align:left;font-size:10px}.valor{text-align:right}.negativo{color:#b73232}@media print{@page{size:landscape;margin:1cm}}</style>
            </head><body>
            <h1>Fluxo de Caixa Mensal</h1>
            <div class="meta"><div>Ano: ${this.periodoDescricaoGerado}</div><div>Saldo Inicial: ${this.formatarMoeda(r.saldoInicialAno)}</div><div>Gerado em: ${geradoEm}</div></div>
            <table><thead><tr><th>Mês</th><th class="valor">Rec. Prev.</th><th class="valor">Rec. Real.</th><th class="valor">Pag. Prev.</th><th class="valor">Pag. Real.</th><th class="valor">Transf. Ent.</th><th class="valor">Transf. Saída</th><th class="valor">Saldo Prev.</th><th class="valor">Saldo Real.</th></tr></thead>
            <tbody>${rows}</tbody>
            </table></body></html>`;
    }

    public downloadCsv(): void {
        if (!this.possuiResultados) return;
        const sep = ';';
        let csv = '';
        if (this.isDiario && this.diarioResponse) {
            const header = ['Data', 'Dia Semana', 'Rec. Previsto', 'Rec. Realizado', 'Pag. Previsto', 'Pag. Realizado', 'Transf. Entrada', 'Transf. Saída', 'Saldo Prev.', 'Saldo Real.'].join(sep);
            const rows = this.diarioItensTabela.map(i => [
                this.formatarData(i.data), i.diaSemana,
                i.recebimentosPrevisto.toFixed(2), i.recebimentos.toFixed(2),
                i.pagamentosPrevisto.toFixed(2), i.pagamentos.toFixed(2),
                i.transferenciasEntrada.toFixed(2), i.transferenciasSaida.toFixed(2),
                i.saldoPrevisto.toFixed(2), i.saldoFinal.toFixed(2)
            ].join(sep));
            csv = '\uFEFF' + [header, ...rows].join('\n');
        } else if (!this.isDiario && this.mensalResponse) {
            const header = ['Categoria Pai', 'Categoria Filha', 'Mes', 'Tipo', 'Valor'].join(sep);
            const rows: string[] = [];
            const categorias = [...this.mensalResponse.categoriasReceita, ...this.mensalResponse.categoriasDespesa];
            for (const cat of categorias) {
                const categoriaPai = this.getCategoriaRaizDescricao(cat, categorias);
                const categoriaFilha = cat.nivel > 0 ? cat.descricao : '';
                for (const idx of this.mensalMesesIndicesTabela) {
                    const mesLabel = this.getResumoMensalByIndex(idx)?.descricaoMes ?? '';
                    rows.push([categoriaPai, categoriaFilha, mesLabel, 'Previsto', cat.previstos[idx].toFixed(2)].join(sep));
                    rows.push([categoriaPai, categoriaFilha, mesLabel, 'Realizado', cat.realizados[idx].toFixed(2)].join(sep));
                }
            }
            csv = '\uFEFF' + [header, ...rows].join('\n');
        }
        if (!csv) return;
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `fluxo-de-caixa-${this.isDiario ? 'diario' : 'mensal'}-${this.periodoDescricaoGerado.replace('/', '-')}.csv`;
        anchor.click();
        URL.revokeObjectURL(url);
    }

    // ─── Formatting ──────────────────────────────────────────────────────────────

    public formatarMoeda(value: number): string {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    }

    public formatarNumero(value: number): string {
        return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
    }

    public formatarData(value: string | Date | null | undefined): string {
        if (!value) return '—';
        // Data de calendário: descarta hora/fuso ("2026-07-06T00:00:00Z" viraria 05/07 no GMT-3)
        const normalizado = typeof value === 'string' ? `${value.split('T')[0]}T00:00:00` : value;
        return this.datePipe.transform(normalizado, 'dd/MM/yyyy') || '—';
    }

    public stripHtml(value: string | null | undefined): string {
        if (!value) return '—';
        const clean = value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        return clean || '—';
    }

    private parseChartDate(value: string | Date | null | undefined): Date | null {
        if (!value) return null;
        if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

        const texto = String(value).trim();
        if (!texto) return null;

        // Data de calendário: descarta hora/fuso para não deslocar o dia (meia-noite UTC → dia anterior no GMT-3)
        const isoBase = `${texto.split('T')[0]}T00:00:00`;
        const isoDate = new Date(isoBase);
        if (!Number.isNaN(isoDate.getTime())) return isoDate;

        const brMatch = texto.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (!brMatch) return null;

        const [, dia, mes, ano] = brMatch;
        const brDate = new Date(Number(ano), Number(mes) - 1, Number(dia));
        return Number.isNaN(brDate.getTime()) ? null : brDate;
    }

    private getDiarioChartLabel(originalValue: string | Date | null | undefined, parsedDate: Date | null): string {
        if (!parsedDate) return this.formatarData(originalValue);

        const meses = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
        return `${String(parsedDate.getDate()).padStart(2, '0')} ${meses[parsedDate.getMonth()]}`;
    }

    public getPeriodoDescricao(): string {
        if (this.isDiario) return this.mesAnoControl.value;
        return this.anoVisualizacaoControl.value;
    }

    // ─── Private helpers ─────────────────────────────────────────────────────────

    private getMesSelecionado(): number {
        if (this.isDiario) {
            const ref = this.parseMesAno(this.mesAnoControl.value);
            return ref ? ref.getMonth() + 1 : new Date().getMonth() + 1;
        }
        return 1;
    }

    private getAnoSelecionado(): number {
        if (this.isDiario) {
            const ref = this.parseMesAno(this.mesAnoControl.value);
            return ref ? ref.getFullYear() : new Date().getFullYear();
        }
        return Number(this.anoVisualizacaoControl.value) || new Date().getFullYear();
    }

    private validarPeriodo(): boolean {
        if (this.isDiario) {
            if (!this.parseMesAno(this.mesAnoControl.value)) {
                this.alertService.show('Atenção', 'Informe um mês/ano válido no formato MM/AAAA.', AlertType.warning);
                return false;
            }
            return true;
        }
        const ano = Number(this.anoVisualizacaoControl.value);
        if (!ano || ano < 2000 || ano > 2100) {
            this.alertService.show('Atenção', 'Informe um ano válido.', AlertType.warning);
            return false;
        }
        return true;
    }

    private limparFiltrosFavorito(): void {
        this.form.patchValue({
            favoritoSelecionado: null,
            mesAno: this.getMesAnoAtual(),
            anoVisualizacao: String(new Date().getFullYear()),
            idContaFinanceira: null,
            idCentroCusto: null
        });
    }

    private carregarFavoritosLocalStorage(): void {
        try {
            const raw = localStorage.getItem(this.favoritosStorageKey);
            this.favoritos = raw ? (JSON.parse(raw) as RelatorioFiltroFavorito[]) : [];
        } catch { this.favoritos = []; }
    }

    private persistirFavoritos(): void {
        if (this.idTipoRelatorio) return;
        localStorage.setItem(this.favoritosStorageKey, JSON.stringify(this.favoritos));
    }

    private getProximoNomeFavorito(): string {
        const nums = this.favoritos.map(f => /^Favorito (\d+)$/.exec(f.nome)?.[1]).filter((v): v is string => !!v).map(Number).filter(v => !Number.isNaN(v));
        const next = nums.length ? Math.max(...nums) + 1 : this.favoritos.length + 1;
        return `Favorito ${next}`;
    }

    private salvarFavorito(nome: string): void {
        const filtro = {
            modoPeriodo: this.granularidadeAtual,
            mesAno: this.mesAnoControl.value,
            dataInicial: null,
            dataFinal: null,
            idCliente: null,
            clienteDescricao: null,
            idContaFinanceira: this.form.get('idContaFinanceira')?.value ?? null,
            idCategoriaFinanceira: null
        };

        if (!this.idTipoRelatorio) {
            const favorito = { idRelatorioFiltroFavorito: Date.now(), idTipoRelatorio: 0, nome, isPadrao: false, datetimeInclusao: new Date().toISOString(), filtro } as RelatorioFiltroFavorito;
            this.favoritos = [favorito, ...this.favoritos];
            this.persistirFavoritos();
            this.form.patchValue({ favoritoSelecionado: String(favorito.idRelatorioFiltroFavorito) });
            return;
        }

        const request: SaveRelatorioFiltroFavoritoRequest = { idTipoRelatorio: this.idTipoRelatorio, nome, isPadrao: false, filtro };
        this.fluxoCaixaService.saveFavorito(request).pipe(takeUntil(this.destroy$)).subscribe({
            next: (response) => {
                if (response.isError || !response.favorito) {
                    this.alertService.show('Erro', response.errorDescription || 'Erro ao salvar favorito.', AlertType.error);
                    return;
                }
                const favorito = response.favorito;
                this.favoritos = [favorito, ...this.favoritos.filter(f => f.idRelatorioFiltroFavorito !== favorito.idRelatorioFiltroFavorito)].sort((a, b) => a.nome.localeCompare(b.nome));
                this.form.patchValue({ favoritoSelecionado: String(favorito.idRelatorioFiltroFavorito) });
            },
            error: () => this.alertService.show('Erro', 'Erro ao salvar favorito.', AlertType.error)
        });
    }

    private removerFavoritoSelecionado(): void {
        const favorito = this.favoritoSelecionadoAtual;
        if (!favorito) return;

        if (!this.idTipoRelatorio) {
            this.favoritos = this.favoritos.filter(f => f.idRelatorioFiltroFavorito !== favorito.idRelatorioFiltroFavorito);
            this.persistirFavoritos();
            this.form.patchValue({ favoritoSelecionado: null });
            return;
        }

        this.fluxoCaixaService.deleteFavorito(favorito.idRelatorioFiltroFavorito).pipe(takeUntil(this.destroy$)).subscribe({
            next: (response) => {
                if (response.isError) { this.alertService.show('Erro', response.errorDescription || 'Erro ao remover favorito.', AlertType.error); return; }
                this.favoritos = this.favoritos.filter(f => f.idRelatorioFiltroFavorito !== favorito.idRelatorioFiltroFavorito);
                this.form.patchValue({ favoritoSelecionado: null });
            },
            error: () => this.alertService.show('Erro', 'Erro ao remover favorito.', AlertType.error)
        });
    }

    private getMesAnoAtual(): string {
        return this.formatMesAno(new Date());
    }

    private formatMesAno(date: Date): string {
        return `${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
    }

    private parseMesAno(value: string | null | undefined): Date | null {
        const match = (value || '').trim().match(/^(\d{2})\/(\d{4})$/);
        if (!match) return null;
        const mes = Number(match[1]);
        const ano = Number(match[2]);
        if (mes < 1 || mes > 12 || ano < 2000 || ano > 2100) return null;
        return new Date(ano, mes - 1, 1);
    }
}
